import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNavigationGuardStore } from '../stores/navigationGuardStore';

type NavigateDirection = 'forward' | 'back';

const DIRECTION_CLASSES: Record<NavigateDirection, string> = {
  forward: 'vt-forward',
  back:    'vt-back',
};

interface NavigateOptions {
  direction?: NavigateDirection;
  types?: string[];
  /** Forwarded to the underlying router navigate — replaces the current history entry instead of pushing a new one. */
  replace?: boolean;
}

export function useViewTransitionNavigate() {
  const navigate = useNavigate();

  /** Returns `true` when the navigation actually proceeded, `false` when a registered
   *  `confirmLeave` guard (`useNavigationLeaveGuard`) cancelled it — callers that only need
   *  fire-and-forget navigation can ignore the return value. */
  return (to: string, options: NavigateOptions | string[] = {}): boolean => {
    // An opt-in guard a page registers via `useNavigationLeaveGuard` (e.g. the print template
    // designer's unsaved-changes prompt). `null` (the default, no page registered) makes this a
    // no-op check for every other navigation in the app.
    const confirmLeave = useNavigationGuardStore.getState().confirmLeave;
    if (confirmLeave && !confirmLeave()) return false;

    const opts: NavigateOptions = Array.isArray(options)
      ? { types: options }
      : options;

    const { direction, types = [], replace } = opts;

    const doc = document as Document & {
      startViewTransition?: (opts: { update: () => void; types?: string[] }) => { finished: Promise<void>; ready: Promise<void> };
    };

    if (typeof doc.startViewTransition !== 'function') {
      navigate(to, { replace });
      return true;
    }

    if (direction) {
      document.documentElement.classList.add(DIRECTION_CLASSES[direction]);
    }

    const transition = doc.startViewTransition({
      update: () => { flushSync(() => navigate(to, { replace })); },
      types,
    });

    if (direction) {
      const dirClass = DIRECTION_CLASSES[direction];
      transition.finished.then(
        () => { document.documentElement.classList.remove(dirClass); },
        () => { document.documentElement.classList.remove(dirClass); },
      );
      transition.ready.catch(() => { document.documentElement.classList.remove(dirClass); });
    }

    return true;
  };
}
